import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { formatNumber } from "$root/localization/formatters";
import { AgGridWithModules, baseGridTheme } from "$root/third-party-integrations/ag-grid/ag-grid";
import { DebouncedSearchInput, Dialog, DialogFooter, Text } from "@mdotm/shadcn/legacy/components";
import { type SpawnResult, adaptAnimatedNodeProvider, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import type { ColDef, IRowNode, RowClassParams, RowDataUpdatedEvent, SelectionChangedEvent } from "ag-grid-community";
import type { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import { useCallback, useMemo, useRef, useState } from "react";
import { LabGroupCellContent, type GroupChildrenSummary } from "./composition-editor/cell/LabGroupCellRenderer";
import type { CompositionRow } from "./composition-editor/rows";
import type { MaybePromise } from "@mdotm/utils/promise";

type NormalizeSelection = { tickerName: string; portfolioUuid?: string };

type NormalizeDialogProps = {
	rows: CompositionRow[];
	decimals: number;
	onSubmitAsync(selected: NormalizeSelection[]): MaybePromise<void>;
};

function NormalizeGroupCellRenderer(params: CustomCellRendererProps<CompositionRow>) {
	const data = params.data;
	const label = data?.label ?? (params.value as string) ?? "";

	const children = params.node.childrenAfterFilter ?? [];
	const { rowType, portfolioName, identifiers } = data ?? {};
	const getChildRowType = (node: (typeof children)[number]) => node.data?.rowType;

	const childrenSummary: GroupChildrenSummary = {
		portfolioCount: children.filter((c) => getChildRowType(c) === "portfolio").length,
		instrumentCount: children.filter((c) => getChildRowType(c) === "instrument").length,
		portfolioSubGroupCount: children.filter((c) => getChildRowType(c) === "subgroup").length,
	};
	return (
		<LabGroupCellContent
			label={label}
			rowType={rowType}
			portfolioName={portfolioName}
			isin={identifiers?.ISIN}
			childrenSummary={childrenSummary}
		/>
	);
}

const normalizeAutoGroupColDef: ColDef<CompositionRow> = {
	headerName: "Name",
	flex: 1,
	cellClass: "composition-group-col",
	cellRendererParams: { suppressCount: true, innerRenderer: NormalizeGroupCellRenderer },
	suppressHeaderMenuButton: true,
	suppressHeaderContextMenu: true,
	// Same copy-the-name alignment as the main grid's auto-column: the tree value defaults to the
	// path key (ticker) while the renderer shows the instrument name.
	valueGetter: (p) => p.data?.label ?? p.node?.key ?? "",
};

function rowKeyOf(row: CompositionRow): string {
	return row.path.join("/");
}

function searchableTextOf(row: CompositionRow): string {
	return [row.label, row.tickerName, row.identifiers?.ISIN, row.portfolioName].filter(Boolean).join(" ");
}

const normalizeTheme = baseGridTheme.withParams({ rowGroupIndentSize: 20 });

// Only instrument rows carry a normalizable weight — group/portfolio rows are containers whose
// selection AG Grid already propagates to their descendants (groupSelects: "descendants"), so
// resolving to instrument keys keeps the operation exactly on what the user selected, in every
// view mode. Deduped because the same ticker may appear once per portfolio path.
function extractSelectedInstruments(selectedRows: CompositionRow[]): NormalizeSelection[] {
	return [
		...new Map(
			selectedRows.flatMap((r) =>
				r.rowType === "instrument" && r.tickerName != null
					? ([
							[
								`${r.portfolioUuid ?? ""}|${r.tickerName}`,
								{ tickerName: r.tickerName, portfolioUuid: r.portfolioUuid },
							],
						] as const)
					: [],
			),
		).values(),
	];
}

function NormalizeDialog({
	show,
	onClose,
	onAnimationStateChange,
	rows,
	decimals,
	onSubmitAsync,
}: NormalizeDialogProps & MinimumDialogProps) {
	const gridRef = useRef<AgGridReact<CompositionRow> | null>(null);

	const { filtered, debouncedQuery, setQuery } = useSearchable({ collection: rows, extractFn: searchableTextOf });

	// Searching replaces the grid's rowData, so display rows keep the ORIGINAL order (fuzzy sort would
	// scramble the tree) and re-include each match's ancestors (so the tree renders real group rows,
	// not filler nodes) and descendants (searching a group shows its instruments).
	const visibleRows = useMemo(() => {
		if (debouncedQuery.trim() === "") {
			return rows;
		}
		const matchedKeys = new Set(filtered.map(rowKeyOf));
		const includeKeys = new Set<string>();
		for (const row of filtered) {
			let key = "";
			for (const segment of row.path) {
				key = key === "" ? segment : `${key}/${segment}`;
				includeKeys.add(key);
			}
		}
		return rows.filter((row) => {
			if (includeKeys.has(rowKeyOf(row))) {
				return true;
			}
			let key = "";
			for (const segment of row.path.slice(0, -1)) {
				key = key === "" ? segment : `${key}/${segment}`;
				if (matchedKeys.has(key)) {
					return true;
				}
			}
			return false;
		});
	}, [rows, filtered, debouncedQuery]);

	// Rows hidden by the search leave the grid model, which would silently drop their checkbox state —
	// the source of truth lives here instead (unlisted = deselected: the dialog opens with an empty
	// selection), mirrored from user interactions and re-applied whenever filtered rows re-enter the grid.
	const selectionByKey = useRef(new Map<string, boolean>());
	const [hasSelection, setHasSelection] = useState(false);

	const syncSelectionFromGrid = useCallback((e: SelectionChangedEvent<CompositionRow>) => {
		// Only mirror USER interactions. Programmatic re-applies (source "api") cover just the visible
		// subset and would overwrite hidden rows' remembered state; the init event fires while the grid
		// is still assembling its model.
		if (e.source === "api" || e.source === "apiSelectAll" || e.source === "gridInitializing") {
			return;
		}
		e.api.forEachNode((node) => {
			if (node.data?.rowType === "instrument") {
				selectionByKey.current.set(rowKeyOf(node.data), node.isSelected() === true);
			}
		});
		setHasSelection([...selectionByKey.current.values()].some(Boolean));
	}, []);

	const reapplySelectionToGrid = useCallback((e: RowDataUpdatedEvent<CompositionRow>) => {
		const toSelect: IRowNode<CompositionRow>[] = [];
		const toDeselect: IRowNode<CompositionRow>[] = [];
		// Only instrument rows are re-applied — group checkboxes derive from their descendants
		// (groupSelects: "descendants"), so setting them here would cascade and fight the map.
		e.api.forEachNode((node) => {
			if (node.data?.rowType !== "instrument") {
				return;
			}
			const wantSelected = selectionByKey.current.get(rowKeyOf(node.data)) ?? false;
			if ((node.isSelected() === true) !== wantSelected) {
				(wantSelected ? toSelect : toDeselect).push(node);
			}
		});
		if (toSelect.length > 0) {
			e.api.setNodesSelected({ nodes: toSelect, newValue: true, source: "api" });
		}
		if (toDeselect.length > 0) {
			e.api.setNodesSelected({ nodes: toDeselect, newValue: false, source: "api" });
		}
	}, []);

	const columnDefs = useMemo<ColDef<CompositionRow>[]>(
		() => [
			{
				colId: "WEIGHT",
				headerName: "%",
				field: "weight",
				width: 90,
				editable: false,
				suppressHeaderMenuButton: true,
				suppressHeaderContextMenu: true,
				valueFormatter: (p) => {
					if (p.value == null || Number.isNaN(p.value as number)) {
						return "N/A";
					}
					return `${formatNumber(p.value as number, decimals)}%`;
				},
			},
		],
		[decimals],
	);

	const _getRowClass = useCallback((params: RowClassParams<CompositionRow>) => {
		const { rowType } = params.data ?? {};
		if (rowType === "group" || rowType === "subgroup" || rowType === "single-instruments") {
			return "!bg-(--palette-N50)";
		}
		if (rowType === "portfolio") {
			return "!bg-(--palette-N20)";
		}
		return undefined;
	}, []);

	async function handleConfirm() {
		// Confirm from the tracked map over ALL rows, not api.getSelectedRows(): with a search active
		// the grid only holds the visible subset, and hidden-but-selected instruments must still count
		// (search is a view filter, not a selection edit).
		const selectedRows = rows.filter((row) => selectionByKey.current.get(rowKeyOf(row)) ?? false);
		// syncComposition never rejects and the atom write is optimistic — close immediately
		// and let the column-header status icon report the background sync (same UX as Update Aum).
		void onSubmitAsync(extractSelectedInstruments(selectedRows));
		onClose?.();
	}

	return (
		<Dialog
			show={show}
			onClose={onClose}
			header="Normalize weights"
			size="xxlarge"
			// The dialog's class list is tailwind-merged, so a plain width here would replace the one
			// `size="xxlarge"` sets. The `!` suffix makes this a distinct class, which survives the merge
			// and wins on specificity.
			className="lg:w-7xl!"
			onSubmitAsync={handleConfirm}
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button onClick={onClose} palette="tertiary" size="small">
							Cancel
						</Button>
					}
					primaryAction={
						<SubmitButton palette="primary" size="small" disabled={!hasSelection}>
							Confirm
						</SubmitButton>
					}
				/>
			}
		>
			<Text type="Body/M/Book" className="mb-4" as="p">
				Select which instruments and portfolios to normalize to 100%.
			</Text>
			<DebouncedSearchInput
				externalQuery=""
				placeholder="Search..."
				className="mb-4"
				data-qualifier="PortfolioLab/NormalizeDialog/Search"
				onChange={setQuery}
			/>
			<div className="h-[60vh]">
				<AgGridWithModules<CompositionRow>
					treeData
					getDataPath={(row) => row.path}
					getRowId={(p) => rowKeyOf(p.data)}
					autoGroupColumnDef={normalizeAutoGroupColDef}
					columnDefs={columnDefs}
					rowData={visibleRows}
					groupDefaultExpanded={-1}
					theme={normalizeTheme}
					rowSelection={{ mode: "multiRow", enableClickSelection: false, groupSelects: "descendants" }}
					onSelectionChanged={syncSelectionFromGrid}
					onRowDataUpdated={reapplySelectionToGrid}
					// getRowClass={getRowClass}
					innerRef={gridRef}
				/>
			</div>
		</Dialog>
	);
}

type SpawnNormalizeDialogProps = NormalizeDialogProps;

function spawnNormalizeDialog(props: SpawnNormalizeDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<NormalizeDialog
				rows={props.rows}
				decimals={props.decimals}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={(selected) => {
					void props.onSubmitAsync(selected);
					resolve();
				}}
			/>
		)),
	);
}

export { NormalizeDialog, spawnNormalizeDialog };
export type { NormalizeDialogProps, NormalizeSelection };
